import { pdf } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { toPng } from "html-to-image";
import JSZip from "jszip";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { A4PDFContent } from "./A4PDFContent";
import { createBackCoverImage, createFrontCoverImage } from "./coverImages";
import { PDFContent } from "./PDFContent";
import { FacilityReportData } from "../../../../models/FacilityReportData";
import DropdownSelect from "../../../FormElement/DropdownSelect";

import "./styles.scss";

type DownloadFormat =
  | "png-zip"
  | "pdf-per-section"
  | "pdf-a4-portrait"
  | "pdf-a4-landscape";

interface IDownloadButtonProps {
  facilityReportData: FacilityReportData;
  isWorking: boolean;
  setIsWorking: (isWorking: boolean) => void;
}

export const DownloadButton: React.FunctionComponent<IDownloadButtonProps> = ({
  facilityReportData,
  isWorking,
  setIsWorking,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string, options?: Record<string, string>) =>
    _t(
      `facilityReportsPage.facilityReportDetails.downloadButton.${key}`,
      options
    );

  const [format, setFormat] = useState<DownloadFormat>("png-zip");

  const sanitizeForFileName = (value: string) =>
    value
      .replace(/[/\\]+/g, "_")
      .replace(/\.\.+/g, "_")
      .trim();

  const sanitizedLocationName = sanitizeForFileName(
    facilityReportData.location.name
  );

  const yearMonthRange = sanitizeForFileName(
    t("year_month_range", {
      startYear: dayjs(facilityReportData.start_date).format("YYYY"),
      startMonth: dayjs(facilityReportData.start_date).format("M"),
      endYear: dayjs(facilityReportData.end_date).format("YYYY"),
      endMonth: dayjs(facilityReportData.end_date).format("M"),
    })
  );

  const honorificSuffix = t("honorific_suffix");

  const baseFileName = `${t(
    "facility_report"
  )}-${sanitizedLocationName}${honorificSuffix}-${yearMonthRange}`;

  const sections = [
    {
      section: "search-keywords",
      id: "facility-report-details-search-keywords-section",
      fileName: `${baseFileName}-${t("section.search_keywords")}.png`,
    },
    {
      section: "website",
      id: "facility-report-details-website-section",
      fileName: `${baseFileName}-${t("section.website")}.png`,
    },
    {
      section: "calls",
      id: "facility-report-details-calls-section",
      fileName: `${baseFileName}-${t("section.calls")}.png`,
    },
    {
      section: "route-search",
      id: "facility-report-details-route-search-section",
      fileName: `${baseFileName}-${t("section.route_search")}.png`,
    },
    {
      section: "impressions",
      id: "facility-report-details-impressions-section",
      fileName: `${baseFileName}-${t("section.impressions")}.png`,
    },
    {
      section: "reviews",
      id: "facility-report-details-reviews-section",
      fileName: `${baseFileName}-${t("section.reviews")}.png`,
    },
    {
      section: "overall-ai-summary",
      id: "facility-report-details-overall-ai-summary-section",
      fileName: `${baseFileName}-${t("section.overall_ai_summary")}.png`,
    },
  ];

  const handleDownloadPDF = async () => {
    setIsWorking(true);
    try {
      const images: { src: string; width: number; height: number }[] = [];

      for (const section of sections) {
        const node = document.getElementById(section.id);
        if (!node) continue;
        const { offsetWidth: width, offsetHeight: height } = node;
        const src = await toPng(node, { pixelRatio: 2 });
        images.push({ src, width, height });
      }

      const blob = await pdf(
        <PDFContent
          images={images}
          facilityName={facilityReportData.location.name}
          honorificSuffix={honorificSuffix}
        />
      ).toBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${baseFileName}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast(t("error_downloading_images"), { type: "error" });
    } finally {
      setIsWorking(false);
    }
  };

  const handleDownloadImage = async () => {
    setIsWorking(true);

    try {
      const zip = new JSZip();
      const imageFolder = zip.folder("images");

      try {
        const frontCoverImage = await createFrontCoverImage(
          honorificSuffix
            ? `${facilityReportData.location.name}　${honorificSuffix}`
            : facilityReportData.location.name
        );
        imageFolder?.file(
          `${baseFileName}-${t("section.cover_front")}.png`,
          frontCoverImage,
          { base64: true }
        );
      } catch (error) {
        console.error("Error creating front cover image:", error);
      }

      for (const section of sections) {
        try {
          const node = document.getElementById(section.id);

          if (!node || !imageFolder) {
            continue;
          }

          const image = await toPng(node);
          const imageData = image.split(",")[1];
          imageFolder.file(section.fileName, imageData, {
            base64: true,
          });
        } catch (error) {
          console.error("Error downloading image:", error);
        }
      }

      try {
        const backCoverImage = await createBackCoverImage();
        imageFolder?.file(
          `${baseFileName}-${t("section.cover_back")}.png`,
          backCoverImage,
          { base64: true }
        );
      } catch (error) {
        console.error("Error creating back cover image:", error);
      }

      const startDateString = dayjs(facilityReportData.start_date).format(
        "YYYY-MM-DD"
      );
      const endDateString = dayjs(facilityReportData.end_date).format(
        "YYYY-MM-DD"
      );
      const zipName = `${sanitizedLocationName}-${startDateString}〜${endDateString}.zip`;

      const blob = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = zipName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading images:", error);
      toast(t("error_downloading_images"), { type: "error" });
    } finally {
      setIsWorking(false);
    }
  };

  const captureChart = async (id: string): Promise<string | undefined> => {
    const node = document.getElementById(id);
    if (!node) return undefined;
    return toPng(node, { pixelRatio: 2 });
  };

  const handleDownloadA4PDF = async (orientation: "portrait" | "landscape") => {
    setIsWorking(true);
    try {
      const [
        website,
        calls,
        routeSearch,
        impressions,
        searchKeywords,
        reviewsCommentsChart,
        reviewsTimeline,
      ] = await Promise.all([
        captureChart("facility-report-details-website-chart"),
        captureChart("facility-report-details-calls-chart"),
        captureChart("facility-report-details-route-search-chart"),
        captureChart("facility-report-details-impressions-chart"),
        captureChart("facility-report-details-search-keywords-chart"),
        captureChart("facility-report-details-reviews-comments-chart"),
        captureChart("facility-report-details-reviews-timeline-chart"),
      ]);

      const blob = await pdf(
        <A4PDFContent
          orientation={orientation}
          facilityReportData={facilityReportData}
          facilityName={facilityReportData.location.name}
          honorificSuffix={honorificSuffix}
          chartImages={{
            website,
            calls,
            routeSearch,
            impressions,
            searchKeywords,
            reviewsCommentsChart,
            reviewsTimeline,
          }}
          sectionTitles={{
            overallAISummary: t("section.overall_ai_summary"),
            website: t("section.website"),
            calls: t("section.calls"),
            routeSearch: t("section.route_search"),
            impressions: t("section.impressions"),
            searchKeywords: t("section.search_keywords"),
            noData: _t("facilityReportsPage.facilityReportDetails.no_data"),
            reviews: {
              title: t("section.reviews"),
              summary: t("section.reviews_summary"),
              improvements: t("section.reviews_improvements"),
              noImprovements: t("section.reviews_no_improvements"),
              timeline: t("section.reviews_timeline"),
              positiveComments: t("section.reviews_positive_comments"),
              negativeComments: t("section.reviews_negative_comments"),
              commentExample: t("section.reviews_comment_example"),
              noComments: t("section.reviews_no_comments"),
            },
          }}
        />
      ).toBlob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${baseFileName}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading A4 PDF:", error);
      toast(t("error_downloading_images"), { type: "error" });
    } finally {
      setIsWorking(false);
    }
  };

  const handleDownload = () => {
    switch (format) {
      case "png-zip":
        handleDownloadImage();
        break;
      case "pdf-per-section":
        handleDownloadPDF();
        break;
      case "pdf-a4-portrait":
        handleDownloadA4PDF("portrait");
        break;
      case "pdf-a4-landscape":
        handleDownloadA4PDF("landscape");
        break;
      default:
        break;
    }
  };

  return (
    <div className="download-control">
      <DropdownSelect
        value={format}
        items={[
          "png-zip",
          "pdf-per-section",
          "pdf-a4-portrait",
          "pdf-a4-landscape",
        ]}
        onChange={(value) => setFormat(value as DownloadFormat)}
        isDisabled={isWorking}
        isTranslation={true}
        translationKey="facilityReportsPage.facilityReportDetails.downloadButton.download"
      />
      <button
        className="btn btn-border download-button"
        onClick={handleDownload}
        type="button"
        disabled={isWorking}
      >
        <span
          aria-hidden={isWorking}
          className={isWorking ? "download-button-hidden" : ""}
        >
          {t("download_label")}
        </span>
        <span
          aria-hidden={!isWorking}
          className={isWorking ? "" : "download-button-hidden"}
        >
          {t("downloading")}
        </span>
      </button>
    </div>
  );
};
